import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Spin } from 'antd';
import saveAs from 'file-saver';
import dayjs from 'dayjs';
import { write, utils } from 'xlsx';
import { RouteComponentProps } from 'react-router-dom';

import {
  ExpenseStatus,
  deleteExpenseAsync,
  expenseStatusKorean,
  getExpenseListAsync,
} from '../../modules/expenses';
import { RootState } from '../../modules';
import { getExpenseType, s2ab } from '../../util/utils';
import Expense3ListComponent from '../../components/expenses/Expense3ListComponent';
import ExpenseDetailDrawer from './ExpenseDetailDrawer';
import ExpenseCreateDrawer from './ExpenseCreateDrawer';

interface Expense3ListMatchParams {
  enterpriseId: string;
  projectId: string;
}

function Expense3ListContainer({
  match,
  history,
}: RouteComponentProps<Expense3ListMatchParams>) {
  const dispatch = useDispatch();
  const { enterpriseId, projectId } = match.params;
  const project = useSelector(
    (state: RootState) => state.projects.project.data,
  );
  const { data, loading, error } = useSelector(
    (state: RootState) => state.expenses.expenses,
  );
  const [visibleDetailDrawer, setVisibleDetailDrawer] =
    useState<boolean>(false);
  const [visibleCreateDrawer, setVisibleCreateDrawer] =
    useState<boolean>(false);
  const [detailDrawerConfig, setDetailDrawerConfig] = useState<any>();
  const [createDrawerConfig, setCreateDrawerConfig] = useState<any>();

  useEffect(() => {
    if (project === null) return;
    dispatch(
      getExpenseListAsync.request({
        enterpriseId,
        projectId,
        filterData: {
          // startDate: dayjs(project.startDate).format('YYYY-MM-DD'),
          // endDate: dayjs(project.endDate).format('YYYY-MM-DD'),
          expenseStatus: ExpenseStatus.SUBMITTED,
        },
      }),
    );
  }, [dispatch, enterpriseId, projectId, project]);

  const onDeleteExpense = (expenseId: string) => {
    dispatch(
      deleteExpenseAsync.request({
        enterpriseId,
        projectId,
        expenseId,
      }),
    );
  };

  const moveToDetailPage = (expenseId: string) => {
    setDetailDrawerConfig({ enterpriseId, projectId, expenseId });
    setVisibleDetailDrawer(true);
  };

  const downloadFile = async (data: any[]) => {
    const _data: any[] = [];
    for (let i = 0; i < data.length; i++) {
      const val: any[] = [];
      val.push(dayjs(data[i].usedDateTime).format('YYYY-MM-DD HH:mm:ss'));
      val.push(data[i].category);
      val.push(data[i].gongjong ?? '기타');
      val.push(getExpenseType(data[i]));
      val.push(expenseStatusKorean[data[i].expenseStatus as ExpenseStatus]);
      val.push(data[i].companyName);
      val.push(data[i].description);
      val.push(data[i].isRefund ? '예' : '아니오');
      val.push(data[i].usedBy ? data[i].usedBy.name : '');
      val.push(data[i].price - data[i].vat);
      val.push(data[i].vat);
      val.push(data[i].price);
      _data.push(val);
    }
    var wb = utils.book_new();
    const ws = await utils.json_to_sheet(_data);
    [
      '날짜',
      '용도',
      '공종',
      '종류',
      '상태',
      '업체명',
      '설명',
      '경비환급',
      '사용자',
      '공급가액',
      '부가세',
      '합계',
    ].forEach((x, idx) => {
      const cellAdd = utils.encode_cell({ c: idx, r: 0 });
      ws[cellAdd].v = x;
    });
    utils.book_append_sheet(wb, ws, `경비내역`);
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    saveAs(
      new Blob([s2ab(wbout)], { type: 'application/octet-stream' }),
      `지출내역서_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.xlsx`,
    );
  };

  const onCloseDetailDrawer = () => setVisibleDetailDrawer(false);
  const moveToCreatePage = () => {
    setCreateDrawerConfig({ enterpriseId, projectId });
    setVisibleCreateDrawer(true);
  };

  return (
    <>
      {loading && <Spin />}
      {error && (
        <Alert
          message={`${error.response?.data.message}`}
          type="error"
          showIcon
          closable
        />
      )}
      {data && (
        <Expense3ListComponent
          data={data}
          onDelete={onDeleteExpense}
          moveToDetailPage={moveToDetailPage}
          downloadFile={downloadFile}
          moveToCreatePage={moveToCreatePage}
        />
      )}
      {detailDrawerConfig && (
        <ExpenseDetailDrawer
          config={detailDrawerConfig}
          visible={visibleDetailDrawer}
          onClose={onCloseDetailDrawer}
        />
      )}
      {createDrawerConfig && (
        <ExpenseCreateDrawer
          config={createDrawerConfig}
          visible={visibleCreateDrawer}
          onClose={() => setVisibleCreateDrawer(false)}
        />
      )}
    </>
  );
}

export default Expense3ListContainer;
